// src/oura/role.ts
//
// Oura has exactly two roles. In production the role comes from the JWT the
// backend returns on /auth/login and /auth/me (userInfo.role). The backend does
// not yet emit the Oura roles, so — outside production — we fall back to a
// dev-only override cookie so both experiences are testable now. See
// handoff-docs for the BE contract this anticipates.

import { useUserProfileContext } from "@context/user_profile";
import { getCookie, setCookie } from "@util/cookie";

export type OuraRole = "oura-staff" | "oura-client";

export const OURA_ROLES: OuraRole[] = ["oura-staff", "oura-client"];

// The backend product/company code an Oura workspace carries
// (workspace_product_type on create, company_type on read-back). Unified with
// the display name on 2026-07-26 while no BE implementation existed, so one
// name covers the marketing/SEO/AEO platform everywhere. The FE only reads
// it, never invents it at runtime.
export const OURA_COMPANY_TYPE = "oura";

// Mock only: which business "belongs to" the signed-in client. Once BE exists,
// the client's business id comes from their workspace, not this constant.
export const OURA_CLIENT_BUSINESS_ID = "nyonya-table";

const DEV_ROLE_COOKIE = "__oura_dev_role__";

export function isOuraRole(role?: string | null): role is OuraRole {
    return role === "oura-staff" || role === "oura-client";
}

/** A platform operator: the roles that run the console, not an Oura workspace. */
export function isPlatformAdmin(role?: string | null): boolean {
    return role === "admin" || role === "super_admin";
}

/**
 * Whether this session may enter /oura/* at all.
 *
 * Deliberately wider than `isOuraRole`. The backend has always counted platform
 * admins as the growth team — `AEO_STAFF_ROLES = (OURA_ROLE_STAFF, "admin",
 * "super_admin")` in dependencies/aeo_auth.py (#3742) — so an admin's session
 * Bearer is authorized on every AEO route. The route guard was stricter than
 * the API it guards, which meant that the moment the product was switched on in
 * production the Growth → Businesses menu appeared for admins and then bounced
 * them to /unauthorized: the API said yes, the router said no.
 *
 * Kept as its own function rather than folded into `isOuraRole`, because that
 * predicate also decides whether a session IS an Oura workspace — widening it
 * would put Oura's two-role settings chrome on every platform admin's own
 * workspace, which is a different claim entirely.
 */
export function mayEnterOura(role?: string | null): boolean {
    return isOuraRole(role) || isPlatformAdmin(role);
}

/** Production is the only environment where role gating is authoritative. */
export function isOuraProd(): boolean {
    return import.meta.env.VITE_APP_ENV === "production";
}

/**
 * Whether the Oura product is exposed at all.
 *
 * Production is opt-IN: every Oura surface stays hidden unless
 * `VITE_ENABLE_OURA` is exactly `"true"`. That lets the code ship to `main`
 * dark and be switched on with an environment change instead of a release —
 * and, just as importantly, keeps it off by default if the variable is ever
 * dropped from the environment. Dev and staging are always on; that is where
 * it is built and QA'd.
 *
 * Deliberately separate from the role gate. Roles decide WHO may enter once
 * the product is live; this decides WHETHER it is live, so a role claim
 * appearing in a JWT earlier than expected cannot expose an unlaunched
 * product on its own.
 */
export function isOuraEnabled(): boolean {
    if (!isOuraProd()) return true;
    return import.meta.env.VITE_ENABLE_OURA === "true";
}

/** Dev/staging only: the role a previewer is currently impersonating. */
export function getDevOuraRole(): OuraRole {
    return getCookie(DEV_ROLE_COOKIE) === "oura-client"
        ? "oura-client"
        : "oura-staff";
}

export function setDevOuraRole(role: OuraRole): void {
    setCookie(DEV_ROLE_COOKIE, role, 30);
}

/**
 * The effective Oura role: the real one from the session if the backend has
 * granted it, otherwise the dev override. Never returns a non-Oura role.
 */
export function useOuraRole(): OuraRole {
    const [, userInfo] = useUserProfileContext();
    if (isOuraRole(userInfo?.role)) return userInfo!.role as OuraRole;
    // A platform admin inside Oura is STAFF, and says so explicitly rather than
    // arriving there through the dev-cookie fallback below — which happens to
    // default to staff today, and would silently hand an admin the client view
    // the moment anyone set that cookie. Production only: outside it the dev
    // switch has to keep winning, because impersonation is how the client
    // experience gets previewed at all.
    if (isOuraProd() && isPlatformAdmin(userInfo?.role)) return "oura-staff";
    return getDevOuraRole();
}

/**
 * Whether the shared settings/member surfaces should use Oura's two-role
 * workspace semantics (Owner + Admin, no read tier, no reviewers).
 *
 * Trust model: in PRODUCTION only the JWT role decides — a user-editable
 * ?product=oura URL can never flip a normal workspace into Oura's
 * admin-only mode. Outside production the URL hint is honored so dev/staging
 * previews (the backend emits no Oura roles yet) can exercise the Oura
 * experience. `roleReady` reflects that the profile loads async: role
 * controls stay locked until detection is resolved.
 */
export function useOuraWorkspace(): { isOuraWs: boolean; roleReady: boolean } {
    const [isLoading, userInfo] = useUserProfileContext();
    const hint =
        !isOuraProd() &&
        new URLSearchParams(window.location.search).get("product") === "oura";
    // With the product switched off in production, the shared Settings page
    // must not render Oura's chrome or its two-role semantics either — the
    // flag has to reach every surface, not just /oura/*.
    if (!isOuraEnabled()) return { isOuraWs: false, roleReady: !isLoading };
    return {
        isOuraWs: hint || (!isLoading && isOuraRole(userInfo?.role)),
        roleReady: hint || !isLoading,
    };
}

/**
 * An Oura merchant on a surface the products share (Settings): an Oura
 * workspace AND the merchant role. Both, because outside production
 * `useOuraRole` falls back to the dev cookie for anyone, so a recruiter or a
 * trainer with that cookie set must not get the merchant's version.
 */
export function useIsOuraMerchant(): boolean {
    const { isOuraWs } = useOuraWorkspace();
    const role = useOuraRole();
    return isOuraWs && role === "oura-client";
}

/**
 * The merchant's screens shown to someone who is not the merchant: the dev
 * role cookie (dev and staging) makes a staff or admin login read as a
 * merchant while every request still carries that staff login. Anything that
 * writes through routes that act on whoever is signed in (the workspace's
 * members, say) must stay shut, or the preview changes the real workspace.
 * A "View as" session is the other preview; it carries its own token and is
 * read from `useViewAs`.
 */
export function useIsOuraMerchantPreview(): boolean {
    const [, userInfo] = useUserProfileContext();
    return useIsOuraMerchant() && userInfo?.role !== "oura-client";
}

/**
 * The merchant's buttons are pills, as on every Oura page. A class to add to
 * a shared `Button`; empty for every other product, so their buttons are
 * untouched.
 */
export function useOuraMerchantButton(): string {
    return useIsOuraMerchant() ? "rounded-full" : "";
}
